import { useEffect, useRef, useState } from "react";
import { confetti, sparkle, stamp } from "../../lib/motion";
import { Button } from "../../components/ui";
import { useVisit } from "./useVisit";
import { useReducedMotion } from "../../lib/reduced-motion";
import "./visit.css";
import { useDialog } from "../../lib/use-dialog";
import { todayISODate } from "@oshimap/utils";


/** 방문 기록 → STAMP. 실패해도 입력을 잃지 않는다. */
export function VisitSheet({ placeId, placeName, onClose, onRecorded }: {
  placeId: string;
  placeName: string;
  onClose: () => void;
  onRecorded?: () => void;
}) {
  const dialog = useDialog(onClose);
  const [visitedOn, setVisitedOn] = useState(todayISODate());
  const [memo, setMemo] = useState("");
  const [rating, setRating] = useState(0);
  const stampRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef<HTMLDivElement>(null);
  const { record, pending, outcome } = useVisit();
  const reduced = useReducedMotion();

  /*
   * 기록이 남은 순간에 STAMP 를 찍는다. 스탬프를 함께 받았으면 SPARKLE 을 겹치고,
   * 3개 이상 한꺼번에 받았으면 CONFETTI 까지 간다 (15 §5 — Achievement).
   * 축하가 과해지지 않게 단계를 둔다.
   */
  useEffect(() => {
    if (!outcome?.ok) return;
    const el = stampRef.current;
    stamp(el, outcome.visitId ?? "visit");
    /* 순서는 CSS animation-delay 가 만든다 — 15 §5 의 오프셋(0 / 320 / 700ms)을 따른다. */
    if (outcome.awarded.length > 0) {
      sparkle(el, 5, 180);
      if (outcome.awarded.length >= 3) confetti(doneRef.current, 28, 260);
    }
  }, [outcome]);

  if (outcome?.ok) {
    return (
      <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label="방문 기록 완료">
        <div ref={doneRef} className="om-visit__done">
          {/* STAMP 는 시그니처 모션이다 — 방문이 남는 순간 반드시 일어난다 (15 §3.3). */}
          <div ref={stampRef} className="om-visit__stamp" aria-hidden="true">
            {outcome.awarded.length > 0 ? "✦" : "✓"}
          </div>
          <h2 className="om-visit__title">{outcome.message}</h2>
          {outcome.awarded.length > 0 ? (
            <p className="om-visit__body">
              {outcome.awarded.map((s) => s.name).join(" · ")} — 여권에 남았습니다.
            </p>
          ) : (
            <p className="om-visit__body">여권의 타임라인에서 다시 볼 수 있어요.</p>
          )}
          {reduced && outcome.awarded.length > 0 ? (
            <p className="om-visit__rm">스탬프 {outcome.awarded.length}개를 받았습니다.</p>
          ) : null}
          <Button variant="primary" block onClick={() => { onRecorded?.(); onClose(); }}>확인</Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label={`${placeName} 방문 기록`}>
      <div className="om-visit__form">
        <h2 className="om-visit__title">{placeName} 방문 기록</h2>

        <label className="om-field">
          <span>다녀온 날</span>
          <input type="date" value={visitedOn} max={todayISODate()} onChange={(e) => setVisitedOn(e.target.value)} />
        </label>

        <fieldset className="om-field om-field--rating">
          <legend>어땠나요?</legend>
          <div className="om-rating" role="radiogroup" aria-label="평점">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" role="radio" aria-checked={rating === n}
                aria-label={`${n}점`} onClick={() => setRating(n)}
                className={rating >= n ? "is-on" : ""}>★</button>
            ))}
          </div>
        </fieldset>

        <label className="om-field">
          <span>메모</span>
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)}
            placeholder="무엇을 얻었는지, 누구와 갔는지" rows={3} />
        </label>

        <p className="om-visit__note">기본 공개 범위는 <b>비공개</b>입니다. 나중에 여권에서 바꿀 수 있어요.</p>

        {outcome && !outcome.ok ? <p className="om-note om-note--err" role="alert">{outcome.message}</p> : null}

        <div className="om-visit__actions">
          <Button variant="ghost" onClick={onClose} disabled={pending}>취소</Button>
          <Button variant="primary" style={{ flex: 1 }} disabled={pending}
            onClick={() => { void record({ placeId, visitedOn, memo: memo || undefined, rating: rating || undefined }); }}>
            {pending ? "기록하는 중…" : "기록하기"}
          </Button>
        </div>
      </div>
    </div>
  );
}
